// Labels taken along to the bin (products.label_before_pick): printed when the pick route is made, at
// the desk where picking starts, or through the picker's own device ("Bu cihazdan bas") when that
// desk's printer computer is off. The walk's labels card reads the route with these.
import type { AsIsOrder, PickRoute } from '@wms/domain';
import { errorMessage } from './api';

/**
 * The route's orders whose label goes along to the bin and is not on a packed parcel yet. One whose label a
 * carrier change made void (acil iş) goes along no more: its stop says not to stick it on, a desk prints the new one.
 */
export const labelsAhead = (route: PickRoute): AsIsOrder[] => route.asIs.filter((o) => o.labelAhead && !o.packed && o.labelVoidAt === undefined);

/**
 * The orders "Bu cihazdan bas" prints. A document that goes into the parcel, or a marketplace label
 * with fewer pages than boxes, keeps the order's label at a desk.
 */
export const printableHere = (route: PickRoute): AsIsOrder[] => labelsAhead(route).filter((o) => o.labelSkipped !== 'documents' && o.labelSkipped !== 'parcels_fixed');

/**
 * The label is not with the picker yet: neither printed on their device nor out of the desk's printer
 * (it waits there, failed, or was never printed). One the desk printed is in hand, its computer off since or not.
 */
export const labelMissing = (o: AsIsOrder): boolean => !o.labelPrintedHere && !o.labelOut;

/** Labels of the orders: a page per box. */
export const labelCount = (orders: readonly AsIsOrder[]): number => orders.reduce((n, o) => n + o.parcels, 0);

/**
 * When the walk shows the labels card: before the first stop; on the way only while the desk's
 * printer is off and a label that goes along is missing (it comes out nowhere else meanwhile).
 */
export function labelsCard(route: PickRoute): 'before' | 'walking' | null {
  if (route.status === 'cancelled' || route.status === 'completed' || labelsAhead(route).length === 0) return null;
  if (route.tasks.every((t) => t.status === 'pending')) return 'before';
  return !route.labelDeskOnline && printableHere(route).some(labelMissing) ? 'walking' : null;
}

/**
 * The desk's printer computer is off (or no desk is set up): what to do with the `labels` missing, as
 * the labels card's title, and how the desk prints them again (under the card's orders: the card stays short).
 */
export function deskDownText(desk: string | null, labels: number): { readonly title: string; readonly hint: string | null } {
  return desk
    ? {
        title: `${desk} yazıcı bilgisayarı kapalı: ${labels} etiketi bu cihazdan basın`,
        hint: `Etiketler masada basılmıyor. Masada basılsın isterseniz masadaki bilgisayarda “Bu bilgisayar ${desk} masasının yazıcısı”nı açın.`,
      }
    : { title: `Paketleme masası tanımlı değil: ${labels} etiketi bu cihazdan basın`, hint: null };
}

/**
 * Asked after "Bu cihazdan bas": the print dialog says nothing of the paper, only the picker knows the
 * labels came out. "Evet" tells the desk not to print them; "Hayır" changes nothing.
 */
export const PRINTED_HERE_QUESTION = {
  title: 'Etiketler yazıcıdan çıktı mı?',
  no: 'Hayır, çıkmadı',
  yes: 'Evet, etiketler elimde',
} as const;

/** What the question says: how many labels went where, and what each answer does at the desk. */
export function printedHereText(count: number, desk: string | null): { readonly sent: string; readonly yes: string; readonly no: string } {
  const printer = desk ? `${desk} masasının yazıcısı` : 'Masanın yazıcısı';
  return {
    sent: `${count} kargo etiketi bu cihazın yazıcısına gönderildi.`,
    yes: `Etiketlerin hepsi elinizdeyse “${PRINTED_HERE_QUESTION.yes}” deyin: ${printer} bunları bir daha basmaz. Rafta ürünlere bu etiketleri yapıştırırsınız.`,
    no: `Yazıcıdan çıkmadıysa ya da eksik çıktıysa “${PRINTED_HERE_QUESTION.no}” deyin: hiçbir şey değişmez, yeniden basabilirsiniz.`,
  };
}

/** Said once the picker answered "Evet": the desk forgot these labels. */
export const printedHereDone = (count: number, desk: string | null): string =>
  `${count} etiket elinizde: ${desk ? `${desk} masasında` : 'masada'} bir daha basılmaz`;

/** Several orders' ZPL labels as one print: each document keeps its own pages (^XA … ^XZ). */
export const joinZpl = (documents: readonly string[]): string =>
  documents
    .map((d) => d.trim())
    .filter((d) => d.length > 0)
    .join('\n');

/** How "Bu cihazdan bas" reaches this device's printer (the packing desk's "Etiketi bas" does the same). */
export interface HerePrinter {
  /** the order's stored shipping label: ZPL as text, or a PDF */
  readonly label: (orderId: string) => Promise<Blob>;
  readonly zpl: (zpl: string) => Promise<unknown>;
  /** rejects when this device cannot print it (no PDF viewer: a phone's browser downloads it instead) */
  readonly pdf: (pdf: Blob) => Promise<void>;
}

/**
 * Each order's stored label through this device's printer: the ZPL ones in one print first, then
 * PDFs one after another. An order whose label cannot be fetched or printed is named and left out;
 * what did go to the printer is still offered for "Etiketler yazıcıdan çıktı mı?".
 */
export async function printLabelsHere(orders: readonly AsIsOrder[], printer: HerePrinter): Promise<{ printed: AsIsOrder[]; failed: string[] }> {
  const zpl: { order: AsIsOrder; text: string }[] = [];
  const pdf: { order: AsIsOrder; blob: Blob }[] = [];
  const printed: AsIsOrder[] = [];
  const failed: string[] = [];
  for (const o of orders) {
    try {
      const blob = await printer.label(o.orderId);
      if (blob.type.startsWith('text/')) zpl.push({ order: o, text: await blob.text() });
      else pdf.push({ order: o, blob });
    } catch (e) {
      failed.push(`${o.orderNumber}: ${errorMessage(e)}`);
    }
  }
  if (zpl.length > 0) {
    try {
      await printer.zpl(joinZpl(zpl.map((z) => z.text)));
      printed.push(...zpl.map((z) => z.order));
    } catch (e) {
      failed.push(...zpl.map((z) => `${z.order.orderNumber}: ${errorMessage(e)}`));
    }
  }
  for (const p of pdf) {
    try {
      await printer.pdf(p.blob);
      printed.push(p.order);
    } catch (e) {
      failed.push(`${p.order.orderNumber}: ${errorMessage(e)}`);
    }
  }
  return { printed, failed };
}
